import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { CallType } from 'matrix-js-sdk/lib/webrtc/call';
import { useTranslation } from 'react-i18next';
import { useWindowWidth } from '@react-hook/window-size';

import { useDialog } from '@edf-pkg/app-main';

import LoadingOverlay from '$app-web/components/chat/components/loading-overlay';
import Draggable from '$app-web/components/draggable';
import { DateTimeFormats, DIALOG_IDS } from '$app-web/constants';
import useTimer from '$app-web/hooks/use-timer';
import Button from '$app-web/ui-kit/components/button';
import ChatIconButton from '$app-web/ui-kit/components/chat-icon-button';
import Dialog from '$app-web/ui-kit/components/dialog';
import IconButton from '$app-web/ui-kit/components/icon-button';
import Tooltip from '$app-web/ui-kit/components/tooltip';
import { appDateTime } from '$app-web/utils';

import AvatarWithBorder from '../../components/avatar-with-border';
import AvatarWithTitle from '../../components/avatar-with-title';
import { VOIP_CALL_STATUS, VOIP_CALL_TYPES } from '../../constants';
import useTones from '../../hooks/use-tones';
import {
    useCurrentStudyUsersData,
    useCurrentUserId,
    useDashboard,
    useDynamicMethods,
    useMethods,
    useVoip,
} from '../../state/chat-client.state';
import { getChatRoomTitle } from '../../utils/chat-client.utils';
import classes from './styles.module.scss';

const ActiveCallBox = () => {
    const { t } = useTranslation();
    const windowWidth = useWindowWidth();

    const methods = useMethods();
    const dashboard = useDashboard();
    const dynamicMethods = useDynamicMethods();
    const { activeCallData, isMinimized } = useVoip();
    const isRecordActionEnabled = activeCallData.status === VOIP_CALL_STATUS.ACTIVE;
    const timer = useTimer();
    const { outgoingCallTone, declinedCallTone } = useTones();
    const currentStudyUsersData = useCurrentStudyUsersData();
    const currentUserId = useCurrentUserId();
    const currentUserData = currentStudyUsersData[currentUserId];

    const minimizedVideo = useRef(null);
    const primaryVideo = useRef(null);
    const secondaryVideo1 = useRef(null);
    const secondaryVideo2 = useRef(null);

    const room = dynamicMethods.getRoom(activeCallData.roomId);

    const recordSubmitDialog = useDialog(DIALOG_IDS.CHAT_RECORD);
    const recordStopDialog = useDialog(DIALOG_IDS.CHAT_RECORD_STOP);
    const recordCallEndDialog = useDialog(DIALOG_IDS.CHAT_RECORD_CALL_END);
    if (activeCallData.status === VOIP_CALL_STATUS.RINGING) {
        outgoingCallTone.paused && outgoingCallTone.play().catch(() => {});
    } else {
        !outgoingCallTone.paused && outgoingCallTone.load();
    }

    useEffect(() => {
        if (
            [VOIP_CALL_STATUS.ACTIVE, VOIP_CALL_STATUS.RINGING].includes(activeCallData.status) &&
            activeCallData.localFeed &&
            secondaryVideo1.current
        ) {
            secondaryVideo1.current.muted = true;
            secondaryVideo1.current.srcObject = activeCallData.localFeed.stream;
            secondaryVideo1.current.play().catch(() => {});
        }
    }, [activeCallData.localFeed, activeCallData.status]);

    useEffect(() => {
        if (
            [VOIP_CALL_STATUS.ACTIVE, VOIP_CALL_STATUS.RINGING].includes(activeCallData.status) &&
            activeCallData.remoteFeed &&
            primaryVideo.current &&
            minimizedVideo.current
        ) {
            primaryVideo.current.srcObject = activeCallData.remoteFeed.stream;
            primaryVideo.current.play().catch(() => {});
            minimizedVideo.current.srcObject = activeCallData.remoteFeed.stream;
            minimizedVideo.current.play().catch(() => {});
        }
    }, [activeCallData.remoteFeed, activeCallData.status]);

    useEffect(() => {
        if (
            [VOIP_CALL_STATUS.ACTIVE].includes(activeCallData.status) &&
            activeCallData.remoteFeed &&
            primaryVideo.current &&
            minimizedVideo.current
        ) {
            const isScreenShared = activeCallData.remoteScreenFeed !== null;
            const primaryStream = isScreenShared ? activeCallData.remoteScreenFeed.stream : activeCallData.remoteFeed.stream;

            primaryVideo.current.srcObject = primaryStream;
            primaryVideo.current.play().catch(() => {});

            minimizedVideo.current.srcObject = primaryStream;
            minimizedVideo.current.play().catch(() => {});

            if (isScreenShared && secondaryVideo2.current) {
                secondaryVideo2.current.srcObject = activeCallData.remoteFeed.stream;
                secondaryVideo2.current.play().catch(() => {});
            }

            if (!isScreenShared && secondaryVideo2.current) {
                secondaryVideo2.current.pause();
                secondaryVideo2.current.srcObject = null;
            }
        }
    }, [activeCallData.remoteFeed, activeCallData.remoteScreenFeed, activeCallData.status]);

    useEffect(() => {
        if (activeCallData.status === VOIP_CALL_STATUS.ACTIVE) {
            // TODO-MAYBE: Caller and receiver timers should be in-sync.
            timer.start();
        } else {
            timer.stop();
        }
    }, [activeCallData.status, timer]);

    const onClickMinimizeButtonHandler = (event) => {
        event.stopPropagation();
        methods.toggleVoipCallMinimized();
    };
    const declinedCallHandler = () => {
        declinedCallTone.play().catch(() => {});
        methods.hangupActiveVoipCall();
        methods.setVoipCallMinimized(false);
    };
    const startRecordCallHandler = () => {
        methods.startRecordingActiveCall();
    };
    const stopRecordCallHandler = () => {
        methods.stopRecordingActiveCall();
    };

    const callButtons = (
        <>
            <ChatIconButton
                icon={activeCallData.audioMuted ? ['fal', 'microphone-slash'] : ['fas', 'microphone']}
                size="small"
                className={classes.buttons}
                color={activeCallData.audioMuted ? 'primary' : 'success'}
                onClick={methods.toggleActiveVoipCallAudio}
                fill
            />
            {activeCallData.type === VOIP_CALL_TYPES.VIDEO && (
                <ChatIconButton
                    icon={activeCallData.videoMuted ? ['fal', 'video-slash'] : ['fas', 'video']}
                    size="small"
                    className={classes.buttons}
                    color={activeCallData.videoMuted ? 'primary' : 'success'}
                    onClick={methods.toggleActiveVoipCallVideo}
                    fill
                />
            )}
            <ChatIconButton
                icon={['fas', 'phone-slash']}
                size="small"
                className={classes.buttons}
                color="error"
                onClick={() => {
                    if (isRecoding && dashboard === 'rdash') {
                        recordCallEndDialog.open();
                    } else {
                        declinedCallHandler();
                    }
                }}
                fill
            />
        </>
    );

    // TODO: We need hours here too.
    const timerAndCalling =
        activeCallData.status === VOIP_CALL_STATUS.RINGING
            ? t('calling')
            : activeCallData.status === VOIP_CALL_STATUS.ACTIVE &&
              `${appDateTime(timer.milliseconds).format(DateTimeFormats.Time, { formatUtc: true })}`;

    const roomTitle = getChatRoomTitle(room, currentStudyUsersData, currentUserId, t);

    const isRecoding = activeCallData.recording;
    const isLoading = activeCallData.isConnecting || activeCallData.isScreenBeingShared;

    return (
        <>
            <Dialog
                title="Record Call"
                id={DIALOG_IDS.CHAT_RECORD}
                maxWidth="xs"
                overPopover
                cancelAction={{
                    onClick: () => recordSubmitDialog.close(),
                    label: t('cancel'),
                }}
                confirmAction={{
                    onClick: () => {
                        startRecordCallHandler();
                        recordSubmitDialog.close();
                    },
                    label: 'Record',
                }}
            >
                <div>
                    During recording, your call recipient will receive a notification about the ongoing recording. The recorded
                    session will be stored in the Recordings tab for a duration of 10 days.
                </div>
            </Dialog>
            <Dialog
                title="Stop Recording Confirmation"
                id={DIALOG_IDS.CHAT_RECORD_STOP}
                maxWidth="xs"
                overPopover
                type="warning"
                onClose={() => recordStopDialog.close()}
                cancelAction={{
                    onClick: () => recordStopDialog.close(),
                    label: t('cancel'),
                }}
                confirmAction={{
                    onClick: () => {
                        stopRecordCallHandler();
                        recordStopDialog.close();
                    },
                    label: 'Stop',
                }}
            >
                <div>Are you sure you want to stop recording?</div>
            </Dialog>
            <Dialog
                title="End Call Confirmation"
                id={DIALOG_IDS.CHAT_RECORD_CALL_END}
                maxWidth="xs"
                type="warning"
                overPopover
                onClose={() => recordCallEndDialog.close()}
                cancelAction={{
                    onClick: () => recordCallEndDialog.close(),
                    label: t('cancel'),
                }}
                confirmAction={{
                    onClick: () => {
                        isRecoding && stopRecordCallHandler();
                        declinedCallHandler();
                        recordCallEndDialog.close();
                    },
                    label: 'End Call',
                }}
            >
                <div>Ending the call will stop the recording. Are you sure you want to end your call?</div>
            </Dialog>
            {[VOIP_CALL_STATUS.ACTIVE, VOIP_CALL_STATUS.RINGING, VOIP_CALL_STATUS.WAITING_FOR_PERMISSION].includes(
                activeCallData.status
            ) && (
                <div className={classes.root}>
                    <Draggable className={classes.draggable} hidden={!isMinimized}>
                        <div className={classes.minimizedVideo}>
                            <AvatarWithBorder avatarColor={room.avatar.color} label={roomTitle} size="sm" src={room.avatar.url} />
                            <video
                                ref={minimizedVideo}
                                className={clsx(
                                    activeCallData.type === VOIP_CALL_TYPES.VIDEO &&
                                        activeCallData.status === VOIP_CALL_STATUS.ACTIVE &&
                                        classes.videoBackground
                                )}
                            />
                            <div className={classes.wrapper}>
                                <div className={classes.draggableFirstButtons}>
                                    <ChatIconButton
                                        icon={['fas', 'expand-alt']}
                                        size="small"
                                        onClick={onClickMinimizeButtonHandler}
                                        fill
                                    />
                                    {isRecoding && (
                                        <Button
                                            className={classes.recordingIndicator}
                                            color="error"
                                            label="Rec"
                                            startIcon={['fas', 'stop-circle']}
                                            size="small"
                                        />
                                    )}
                                </div>
                                <div className="flex justify-center ">{callButtons}</div>
                            </div>
                        </div>
                    </Draggable>

                    <div hidden={isMinimized}>
                        <div className={classes.rootActive}>
                            <div className={classes.header}>
                                <div>
                                    <ChatIconButton
                                        icon={['fas', 'compress-alt']}
                                        size="small"
                                        className={clsx(classes.smHidden)}
                                        onClick={onClickMinimizeButtonHandler}
                                        fill
                                    />
                                    {isRecoding && dashboard === 'pdash' && (
                                        <Button
                                            className={classes.recordingIndicator}
                                            color="error"
                                            label="Rec"
                                            startIcon={['fas', 'stop-circle']}
                                            size="small"
                                        />
                                    )}
                                </div>

                                <ChatIconButton
                                    icon={['fal', 'angle-left']}
                                    size="small"
                                    className={classes.iconBack}
                                    onClick={onClickMinimizeButtonHandler}
                                    color="secondary"
                                />

                                <AvatarWithTitle
                                    avatarColor={room.avatar.color}
                                    avatarLabel={roomTitle}
                                    avatarSize="sm"
                                    src={room.avatar.url}
                                    subtitle={
                                        windowWidth >= 960
                                            ? t('from_study_with_placeholder', [methods.getStudyName(room.studyId)])
                                            : timerAndCalling
                                    }
                                    title={dynamicMethods.getRoomTitle(activeCallData.roomId) || ''}
                                />

                                {windowWidth >= 960 && <span className={classes.timer}>{timerAndCalling}</span>}
                            </div>
                            <div className={classes.center}>
                                <LoadingOverlay
                                    callParticipantName={roomTitle}
                                    loading={isLoading}
                                    mode={activeCallData.isScreenBeingShared ? 'screen-share' : 'connecting'}
                                />

                                <AvatarWithBorder
                                    avatarColor={room.avatar.color}
                                    classes={{ border: classes.largeAvatarWithBorder }}
                                    label={roomTitle}
                                    size="lg"
                                    src={room.avatar.url}
                                />

                                <video
                                    className={clsx(
                                        classes.primaryVideo,
                                        activeCallData.type === VOIP_CALL_TYPES.VIDEO &&
                                            activeCallData.status === VOIP_CALL_STATUS.ACTIVE &&
                                            classes.videoBackground
                                    )}
                                    ref={primaryVideo}
                                />

                                <div className={classes.secondaryVideos}>
                                    {activeCallData.remoteScreenFeed !== null && (
                                        <div>
                                            <AvatarWithBorder
                                                avatarColor={room.avatar.color}
                                                label={roomTitle}
                                                size="sm"
                                                src={room.avatar.url}
                                            />
                                            <video
                                                className={clsx(
                                                    activeCallData.type === VOIP_CALL_TYPES.VIDEO &&
                                                        activeCallData.status === VOIP_CALL_STATUS.ACTIVE &&
                                                        classes.videoBackground
                                                )}
                                                ref={secondaryVideo2}
                                            />
                                        </div>
                                    )}
                                    <div className={classes.localPreview}>
                                        {/* FIXME: This should use the current user's data. Will be done after the refactoring. */}
                                        <AvatarWithBorder
                                            avatarColor={currentUserData?.avatar.color}
                                            label={currentUserData?.label}
                                            size="sm"
                                            src={currentUserData?.avatar.url}
                                        />
                                        <video
                                            className={clsx(
                                                activeCallData.type === VOIP_CALL_TYPES.VIDEO &&
                                                    activeCallData.status === VOIP_CALL_STATUS.ACTIVE &&
                                                    classes.videoBackground
                                            )}
                                            ref={secondaryVideo1}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={classes.panel}>
                                {dashboard === 'rdash' && (
                                    <>
                                        {callButtons}
                                        <div className={classes.toggleButton}>
                                            {isRecoding && (
                                                <>
                                                    <IconButton
                                                        icon={['fas', 'stop-circle']}
                                                        color="error"
                                                        className={classes.recordStopButton}
                                                        onClick={() => recordStopDialog.open()}
                                                    />
                                                    <span>Stop Recording</span>
                                                </>
                                            )}
                                            {!isRecoding && activeCallData.type === CallType.Video && (
                                                <>
                                                    <Tooltip
                                                        disabled={isRecordActionEnabled}
                                                        side="left"
                                                        maxWidth={168}
                                                        content={t('rdash:record_call_disabled')}
                                                    >
                                                        {/* Do not remove the following div wrapper, it is a hack to make it
                                                            possible to show the tooltip while the icon button is disabled: */}
                                                        <div>
                                                            <IconButton
                                                                disabled={!isRecordActionEnabled}
                                                                icon={['fas', 'record-vinyl']}
                                                                color="ghost"
                                                                className={classes.recordStopButton}
                                                                onClick={() => recordSubmitDialog.open()}
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                    <span>Record Call</span>
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                                {dashboard !== 'rdash' && callButtons}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

ActiveCallBox.propTypes = {};

export default ActiveCallBox;
